import React, { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import { DISPOSITIONS, LABELS } from '../../../shared/creatureDisposition';
import './DmPartiesTool.scss';

const initials = name => String(name || '?').trim().slice(0, 2).toUpperCase();

/** Puts everyone not yet in a party into the first one, and drops anyone who has left. */
export const arrangeParties = (stored = [], characters = []) => {
  const ids = characters.map(character => String(character.id));
  const rows = (stored.length ? stored : [{ characterIds: [] }]).map(party => (party.characterIds || []).map(String).filter(id => ids.includes(id)));
  const placed = new Set(rows.flat());
  rows[0] = [...rows[0], ...ids.filter(id => !placed.has(id))];
  return rows;
};

/** Moves a character to another row, leaving the rest in order. */
export const moveToParty = (rows, id, toRow) => rows.map((row, index) => [...row.filter(item => item !== id), ...(index === toRow ? [id] : [])]);

// DM Tools → Parties: characters in a row are allies, and each row after the first is Ally, Neutral or Enemy to the first,
// the same setting a monster has. Drag a figure to another row; clicking one and then a row does the same on a touch screen.
export default function DmPartiesTool({ campaignId, characters = [], renderTrigger }) {
  const [open, setOpen] = useState(false), [rows, setRows] = useState([[]]), [settings, setSettings] = useState([undefined]), [revision, setRevision] = useState(0);
  const [picked, setPicked] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const url = `/campaigns/${encodeURIComponent(campaignId)}/parties`;
  const byId = new Map(characters.map(character => [String(character.id), character]));
  const openDialog = async () => {
    setError(''); setPicked(null); setOpen(true);
    try {
      const response = await apiFetch(url, { cache: 'no-store' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      const arranged = arrangeParties(data.parties, characters);
      setRows(arranged); setSettings(arranged.map((_, index) => data.parties[index]?.disposition)); setRevision(data.revision);
    } catch (failure) { setError(failure.message || 'Parties could not be loaded.'); }
  };
  const move = (id, row) => { setRows(old => moveToParty(old, id, row)); setPicked(null); };
  const save = async () => {
    setBusy(true); setError('');
    try {
      const response = await apiFetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parties: rows.map((characterIds, index) => ({ characterIds, ...(index ? { disposition: settings[index] || 'neutral' } : {}) })).filter((party, index) => party.characterIds.length || index === 0), revision }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setOpen(false);
    } catch (failure) { setError(failure.message || 'Parties could not be saved.'); } finally { setBusy(false); }
  };
  return <>
    {renderTrigger(openDialog)}
    <Modal show={open} onHide={() => setOpen(false)} centered size="lg" className="dnd-modal modern-modal" aria-label="Parties">
      <Modal.Header closeButton><Modal.Title>Parties</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="dm-parties">
          {rows.map((row, index) => <section key={index} className="dm-parties__row" aria-label={`Party ${index + 1}`} onClick={() => { if (picked) move(picked, index); }}
            onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); const id = event.dataTransfer.getData('text/plain'); if (id) move(id, index); }}>
            <header className="dm-parties__label"><span>Party {index + 1}</span>
              {index > 0 && <Form.Select size="sm" className="dm-parties__setting" aria-label={`Party ${index + 1} is to Party 1`} value={settings[index] || 'neutral'} onClick={event => event.stopPropagation()}
                onChange={event => { const value = event.target.value; setSettings(old => old.map((item, at) => (at === index ? value : item))); }}>
                {DISPOSITIONS.map(value => <option key={value} value={value}>{LABELS[value]} to Party 1</option>)}
              </Form.Select>}
              {index > 0 && !row.length && <button type="button" className="dm-parties__remove" onClick={event => { event.stopPropagation(); setRows(old => old.filter((_, at) => at !== index)); setSettings(old => old.filter((_, at) => at !== index)); }}>Remove</button>}
            </header>
            <div className="dm-parties__figures">
              {row.map(id => { const character = byId.get(id); return <button key={id} type="button" draggable aria-pressed={picked === id} className={`dm-parties__figure${picked === id ? ' is-picked' : ''}`}
                onDragStart={event => event.dataTransfer.setData('text/plain', id)} onClick={event => { event.stopPropagation(); setPicked(picked === id ? null : id); }}>
                {character?.image ? <img src={character.image} alt="" /> : <span className="dm-parties__initials" aria-hidden="true">{initials(character?.name)}</span>}
                <span className="dm-parties__name">{character?.name || id}</span>
              </button>; })}
              {!row.length && <span className="dm-parties__empty">Drag a character here</span>}
            </div>
          </section>)}
        </div>
        <p className="small mt-2 mb-0">Characters in the same row are allies. Other rows are set against Party 1, and a monster’s own setting works the same way.</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" className="me-auto" onClick={() => { setRows(old => [...old, []]); setSettings(old => [...old, 'neutral']); }}>New party</Button>
        <Button variant="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
        <Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button>
      </Modal.Footer>
    </Modal>
  </>;
}
